// src/hooks/useSocketRefreshButton.ts
// Central hook for handling socket:client:ShowRefreshButton notifications.
// Subscribes to the event, validates connection ID, deduplicates, batches via debounce,
// and exposes hasPendingUpdates + triggerRefresh to the host page.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'; // useMemo kept for pendingGroupIds
import { useSocketEvent } from './useSocketEvent';
import { socketService } from '@/services/socketService';
import { conversationBus } from '@/lib/conversationBus';
import type {
  ShowRefreshButtonPayload,
  PendingUpdate,
  NotificationRelevance,
} from '@/types/socketRefresh';

/** Types that affect tab/dashboard list — processed automatically without user click */
const DASHBOARD_LIST_TYPES = new Set([
  'NEWDASHBOARD',
  'NEWDASHBOARDTAB',
  'DASHBOARDDELETED',
  'REMOVETABDASHBOARD',
  'DASHBOARDUNSHARED',
]);

/** Types that affect dashboard content (cards / groups) — require user click to refresh */
const CONTENT_TYPES = new Set([
  'UPDATEDASHBOARDITEM',
  'NEWDEFITEM',
  'MOVECONVTOCOLUMN',
  'DELDASHBOARDGROUPCONV',
  'DELDASHBOARDGROUP',
  'UPDDASHBOARDGROUP',
  'DASHBOARDGROUPPOS',
  'NEWDASHBOARDGROUP',
  'DYNAMICDASHBOARDGROUP',
]);

/** Types where the change is card-level: highlight the card only, not its columns */
const CARD_ONLY_TYPES = new Set(['MOVECONVTOCOLUMN']);

/** Types where the notified card was edited/added and should be shown first in its column after refresh */
const MOVE_TO_TOP_TYPES = new Set(['UPDATEDASHBOARDITEM', 'NEWDEFITEM', 'MOVECONVTOCOLUMN']);

/** ConversationIds from pending updates that should be moved to the top of their column on refresh */
export function getMoveToTopConversationIds(pendingUpdates: PendingUpdate[]): number[]
{
  const ids = new Set<number>();
  pendingUpdates.forEach(({ payload }) =>
  {
    if (MOVE_TO_TOP_TYPES.has(payload.type) && payload.conversationId)
    {
      ids.add(payload.conversationId);
    }
  });
  return [...ids];
}

/**
 * True when the conversation is part of the current dashboard: either in the loaded set
 * or rendered as a card (children expanded later are not in the loaded set).
 * Mirrors legacy `li[data-conversationId=...]` lookup.
 */
function isConversationInView(conversationId: number, visibleConversationIds?: Set<number>): boolean
{
  if (visibleConversationIds?.has(conversationId)) return true;
  return document.querySelector(`.dashboard-groups [data-conversationid="${conversationId}"]`) != null;
}

/** Debounce window for showing the refresh button after content notifications (ms) */
const CONTENT_DEBOUNCE_MS = 3500;

export interface UseSocketRefreshButtonOptions {
  /** DashboardId of the currently visible dashboard. null = HomeView (any dashboard). */
  currentDashboardId: number | null;
  /** configValueId of the active view (1=Dashboards, 148=Bookmarks, 9/15/...=Config). */
  configValueId?: number;
  /** True when the view shows a project's dashboard. */
  isProjectView?: boolean;
  /** projectId when isProjectView=true. */
  projectId?: number | null;
  /** True when the current dashboard has dynamic groups (IsDefault=true). */
  hasDynamicGroups?: boolean;
  /**
   * Set of conversationIds currently visible in the dashboard.
   * Used for filtering notifications when there are no dynamic groups.
   */
  visibleConversationIds?: Set<number>;
  /**
   * Called when the user clicks the Refresh button (or when auto-refresh is safe).
   * Receives the array of pending updates so the caller knows what to reload.
   */
  onRefresh: (pendingUpdates: PendingUpdate[]) => void;
  /**
   * Called automatically (debounced) when dashboard-list changes arrive
   * (NEWDASHBOARD, DASHBOARDDELETED, etc.) — reload TabControl silently.
   */
  onRefreshDashboardList?: (pendingUpdates: PendingUpdate[]) => void;
  /**
   * Called when the currently active dashboard was deleted or unshared.
   * The caller should navigate to another dashboard.
   */
  onDashboardRemoved?: (dashboardId: number) => void;
  /**
   * Called when the user clicks refresh after an OrderDashboard event.
   * Should reload the tab list WITHOUT changing the selected tab (reloadDashboard(false)).
   */
  onRefreshDashboardOrder?: () => void;
}

export interface UseSocketRefreshButtonResult {
  /** True when there are content updates waiting for the user to refresh */
  hasPendingUpdates: boolean;
  /** Number of pending content updates */
  pendingCount: number;
  /** Manually clear the pending queue */
  clearPending: () => void;
  /** Clear ALL yellow highlights (columns + cards) */
  clearHighlight: () => void;
  /** Clear yellow highlight for a single conversation card */
  clearConversationHighlight: (conversationId: number) => void;
  /** Process pending updates and clear the queue */
  triggerRefresh: () => void;
  /** True when a tab-order change (OrderDashboard) is pending — different message shown */
  pendingOrderDashboard: boolean;
  /** True when a dashboard-list change (new/deleted/unshared tab) is pending */
  pendingDashboardList: boolean;
  /** DashboardGroupIds highlighted yellow after refresh — auto-cleared after timer */
  pendingGroupIds: Set<number>;
  /** ConversationIds highlighted yellow after refresh — cleared per-card on infowindow open */
  pendingConversationIds: Set<number>;
}

export function useSocketRefreshButton(
  options: UseSocketRefreshButtonOptions,
): UseSocketRefreshButtonResult
{
  const {
    currentDashboardId,
    configValueId,
    isProjectView,
    projectId,
    hasDynamicGroups,
    visibleConversationIds,
    onRefresh,
    onRefreshDashboardList,
    onDashboardRemoved,
    onRefreshDashboardOrder,
  } = options;

  // State for the UI (hasPendingUpdates / count)
  const [contentPendingCount, setContentPendingCount] = useState(0);

  // Pending dashboard-order update (socket:client:OrderDashboard)
  // Kept separate from contentPendingCount — its refresh reloads the tab list, not the groups.
  const pendingOrderDashboardRef = useRef(false);
  const [pendingOrderDashboard, setPendingOrderDashboard] = useState(false);

  // Pending dashboard-list change (new/deleted/unshared tab) — shows the refresh button
  // instead of auto-applying, which was jumping the selection to the last tab.
  const pendingDashboardListRef = useRef(false);
  const [pendingDashboardList, setPendingDashboardList] = useState(false);

  // Group and conversation IDs shown with yellow highlight AFTER the user clicks refresh
  // Cleared when the user clicks any card (opens infowindow) or reloads the page
  const [highlightGrpIds, setHighlightGrpIds] = useState<number[]>([]);
  const [highlightConvIds, setHighlightConvIds] = useState<number[]>([]);

  // Refs to avoid stale closures inside the socket handler
  const pendingRef = useRef<PendingUpdate[]>([]);
  const contentDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightGrpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const onRefreshDashboardListRef = useRef(onRefreshDashboardList);
  const onDashboardRemovedRef = useRef(onDashboardRemoved);
  const onRefreshDashboardOrderRef = useRef(onRefreshDashboardOrder);

  // Keep callback refs up to date without re-subscribing to socket
  useEffect(() =>
  {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  useEffect(() =>
  {
    onRefreshDashboardListRef.current = onRefreshDashboardList;
  }, [onRefreshDashboardList]);
  useEffect(() =>
  {
    onDashboardRemovedRef.current = onDashboardRemoved;
  }, [onDashboardRemoved]);
  useEffect(() =>
  {
    onRefreshDashboardOrderRef.current = onRefreshDashboardOrder;
  }, [onRefreshDashboardOrder]);

  // Clear queue when the active dashboard changes
  useEffect(() =>
  {
    pendingRef.current = [];
    setContentPendingCount(0);
    pendingOrderDashboardRef.current = false;
    setPendingOrderDashboard(false);
    pendingDashboardListRef.current = false;
    setPendingDashboardList(false);
    setHighlightGrpIds([]);
    setHighlightConvIds([]);
    if (contentDebounceTimerRef.current)
    {
      clearTimeout(contentDebounceTimerRef.current);
      contentDebounceTimerRef.current = null;
    }
    if (highlightGrpTimerRef.current)
    {
      clearTimeout(highlightGrpTimerRef.current);
      highlightGrpTimerRef.current = null;
    }
  }, [currentDashboardId]);

  // Cleanup timers on unmount
  useEffect(() =>
  {
    return () =>
    {
      if (contentDebounceTimerRef.current) clearTimeout(contentDebounceTimerRef.current);
      if (highlightGrpTimerRef.current) clearTimeout(highlightGrpTimerRef.current);
    };
  }, []);

  /** Classify a notification relative to the current view */
  const classifyRelevance = useCallback(
    (payload: ShowRefreshButtonPayload): NotificationRelevance =>
    {
      const { type, dashboardId } = payload;

      // Dashboard-list changes
      if (DASHBOARD_LIST_TYPES.has(type))
      {
        // Active dashboard deleted/unshared → immediate removal action
        if (
          (type === 'DASHBOARDDELETED' || type === 'DASHBOARDUNSHARED') &&
          dashboardId != null &&
          dashboardId === currentDashboardId
        )
        {
          return 'remove-dashboard';
        }
        return 'reload-dashboard-list';
      }

      // Content changes
      if (CONTENT_TYPES.has(type))
      {
        // HomeView: currentDashboardId === null → accept most content events,
        // but column-layout events (MOVECONVTOCOLUMN, DASHBOARDGROUPPOS) are not
        // relevant to Home sections (My Cards, Due Timeline, etc.).
        if (
          currentDashboardId === null &&
          (type === 'MOVECONVTOCOLUMN' || type === 'DASHBOARDGROUPPOS')
        )
        {
          return 'ignore';
        }

        if (currentDashboardId !== null)
        {
          // Ignore if the event is for a different dashboard.
          // When viewing a specific dashboard, only accept events that explicitly match this dashboard.
          // Exceptions:
          // 1. If dashboard has dynamic groups (IsDefault=true), allow events with dashboardId=null
          //    because they might affect the dynamic groups (query-based).
          // 2. In ProjectView with NEWDEFITEM: accept if has dynamic groups OR dashboardId matches.
          // 3. If in ProjectView without dynamic groups, check if conversationId is visible in dashboard.
          // 4. NEWDEFITEM for a child task (created from the parent's Info Window): the payload carries
          //    no dashboardId, so accept it when the parent card (relatedConversationId) is in this dashboard.
          if (dashboardId == null || dashboardId !== currentDashboardId)
          {
            // Allow null dashboardId if this dashboard has dynamic groups
            if (dashboardId === null && hasDynamicGroups)
            {
              // Dynamic groups can pull cards based on queries, so null dashboardId events are relevant
            }
            else if (
              type === 'NEWDEFITEM' &&
              payload.relatedConversationId &&
              isConversationInView(payload.relatedConversationId, visibleConversationIds)
            )
            {
              // New child task whose parent card is shown in this dashboard
            }
            // In ProjectView with NEWDEFITEM: only accept if has dynamic groups
            // (without dynamic groups, NEWDEFITEM must have matching dashboardId which was already checked above)
            else if (isProjectView && type === 'NEWDEFITEM' && hasDynamicGroups)
            {
              // New card with dynamic groups: might appear based on query criteria
            }
            // In ProjectView without dynamic groups, check if conversationId is in the visible set
            else if (
              isProjectView &&
              !hasDynamicGroups &&
              visibleConversationIds &&
              payload.conversationId != null
            )
            {
              if (!visibleConversationIds.has(payload.conversationId))
              {
                return 'ignore';
              }
              // conversationId is visible, allow it through
            }
            else
            {
              return 'ignore';
            }
          }
        }

        // For project views: accept if projectId matches OR conversationId is visible
        if (isProjectView && projectId != null)
        {
          // Normalize null/undefined/0 as "no project" (treat as 0)
          const payloadProject = (payload.projectId ?? payload.ProjectId) ?? 0;

          // If payload doesn't have a matching projectId
          if (payloadProject !== projectId)
          {
            // Check if conversationId is visible in the current dashboard
            if (visibleConversationIds && payload.conversationId != null)
            {
              if (!visibleConversationIds.has(payload.conversationId))
              {
                return 'ignore';
              }
              // conversationId is visible, allow it through even though projectId doesn't match
            }
            else
            {
              return 'ignore';
            }
          }
        }

        // Filter BOOKMARK notifications: only relevant when personId matches current user
        if (payload.notificationTypeCode === 'BOOKMARK')
        {
          const storedPersonId = parseInt(localStorage.getItem('C2_personid') || '0', 10);
          if (payload.personId !== storedPersonId) return 'ignore';
        }

        // Ignore MOVECONVTOCOLUMN if card stayed in same column
        if (
          type === 'MOVECONVTOCOLUMN' &&
          payload.pParentId === payload.nParentId &&
          payload.nDashboardGroupId === payload.pDashboardGroupId
        )
        {
          return 'ignore';
        }

        // configValueId filter: if specified, event must match (for NEWDEFITEM cross-config checks)
        if (
          configValueId != null &&
          configValueId !== 1 &&
          payload.ConfigValueId != null &&
          payload.ConfigValueId !== configValueId
        )
        {
          return 'ignore';
        }

        return 'reload-groups';
      }

      return 'ignore';
    },
    [currentDashboardId, isProjectView, projectId, configValueId, hasDynamicGroups, visibleConversationIds],
  );

  const handleSocketEvent = useCallback(
    (payload: ShowRefreshButtonPayload) =>
    {
      // Discard own actions (same socket connection)
      if (
        payload.SocketConnectionId &&
        payload.SocketConnectionId !== '' &&
        payload.SocketConnectionId === socketService.connectionId
      )
      {
        return;
      }

      const relevance = classifyRelevance(payload);

      if (relevance === 'ignore') return;

      if (relevance === 'remove-dashboard')
      {
        if (onDashboardRemovedRef.current && payload.dashboardId != null)
        {
          onDashboardRemovedRef.current(payload.dashboardId);
        }
        return;
      }

      if (relevance === 'reload-dashboard-list')
      {
        // Show the refresh button; reload happens on click, preserving the selected tab.
        pendingDashboardListRef.current = true;
        setPendingDashboardList(true);
        return;
      }

      // Build dedup key
      const dedupKey = `${payload.type}-${payload.dashboardId ?? 'null'}-${payload.conversationId ?? 'null'}-${payload.dashboardGroupId ?? 'null'}`;

      const isDuplicate = pendingRef.current.some(u => u.id === dedupKey);
      if (isDuplicate) return;

      const newUpdate: PendingUpdate = {
        id: dedupKey,
        payload,
        receivedAt: Date.now(),
      };

      pendingRef.current = [...pendingRef.current, newUpdate];

      if (relevance === 'reload-groups')
      {
        // Debounce showing the refresh button to batch close-together notifications
        if (contentDebounceTimerRef.current) clearTimeout(contentDebounceTimerRef.current);
        contentDebounceTimerRef.current = setTimeout(() =>
        {
          const contentCount = pendingRef.current.filter(u => CONTENT_TYPES.has(u.payload.type)).length;
          setContentPendingCount(contentCount);
          contentDebounceTimerRef.current = null;
        }, CONTENT_DEBOUNCE_MS);
      }
    },
    [classifyRelevance],
  );

  useSocketEvent<ShowRefreshButtonPayload>('socket:client:ShowRefreshButton', handleSocketEvent);

  // ─── OrderDashboard ────────────────────────────────────────────────────────
  // Fired by the server when another user reorders dashboard TABS (not groups/columns).
  // Legacy payload: { ProjectId, dashboards[], socketConnectionId, draggadedDashboard }
  // Filter: same project (ProjectId match) + not own action.
  // On click: reloads the tab list via onRefreshDashboardList (not the groups).
  const handleOrderDashboardEvent = useCallback(
    (payload: {
      ProjectId?: number | null;
      socketConnectionId?: string;
      dashboards?: Array<{ DashboardId: number; Order: number } | null> | null;
    }) =>
    {
      // Discard own actions (legacy uses lowercase 'socketConnectionId')
      if (payload.socketConnectionId && payload.socketConnectionId === socketService.connectionId)
      {
        return;
      }
      // Match same project: treat null/undefined/0 as "global" (no project).
      // Only show notification if both sides are in the same project context.
      const currentProjectId = projectId ?? 0;
      const eventProjectId = payload.ProjectId ?? 0;
      if (currentProjectId !== eventProjectId)
      {
        return;
      }
      pendingOrderDashboardRef.current = true;
      setPendingOrderDashboard(true);
    },
    [projectId],
  );

  useSocketEvent<{
    ProjectId?: number | null;
    socketConnectionId?: string;
    dashboards?: Array<{ DashboardId: number; Order: number }> | null;
  }>('socket:client:OrderDashboard', handleOrderDashboardEvent);

  const clearPending = useCallback(() =>
  {
    pendingRef.current = [];
    setContentPendingCount(0);
    pendingOrderDashboardRef.current = false;
    setPendingOrderDashboard(false);
    pendingDashboardListRef.current = false;
    setPendingDashboardList(false);
    if (contentDebounceTimerRef.current)
    {
      clearTimeout(contentDebounceTimerRef.current);
      contentDebounceTimerRef.current = null;
    }
    if (highlightGrpTimerRef.current)
    {
      clearTimeout(highlightGrpTimerRef.current);
      highlightGrpTimerRef.current = null;
    }
    setHighlightGrpIds([]);
    setHighlightConvIds([]);
  }, []);

  const triggerRefresh = useCallback(() =>
  {
    const toProcess = [...pendingRef.current];
    const hasOrderUpdate = pendingOrderDashboardRef.current;
    const hasDashboardListUpdate = pendingDashboardListRef.current;

    // Extract IDs from pending payloads to highlight after refresh
    const grpIds: number[] = [];
    const convIds: number[] = [];
    toProcess.forEach(({ payload }) =>
    {
      // Card moves highlight only the moved card — the source and target columns
      // themselves did not change, so p/nDashboardGroupId are skipped here.
      if (!CARD_ONLY_TYPES.has(payload.type))
      {
        if (payload.dashboardGroupId && !grpIds.includes(payload.dashboardGroupId))
          grpIds.push(payload.dashboardGroupId);
        if (payload.pDashboardGroupId && !grpIds.includes(payload.pDashboardGroupId))
          grpIds.push(payload.pDashboardGroupId);
        if (payload.nDashboardGroupId && !grpIds.includes(payload.nDashboardGroupId))
          grpIds.push(payload.nDashboardGroupId);
      }

      if (payload.conversationId && !convIds.includes(payload.conversationId))
        convIds.push(payload.conversationId);
    });

    // If any updated conversation is currently open in the infowindow, reload it
    convIds.forEach(convId =>
    {
      conversationBus.emit('dashboard-refresh', { conversationId: convId });
    });

    // Clear pending queues & button
    pendingRef.current = [];
    setContentPendingCount(0);
    pendingOrderDashboardRef.current = false;
    setPendingOrderDashboard(false);
    pendingDashboardListRef.current = false;
    setPendingDashboardList(false);

    // Activate yellow highlight:
    //   - Columns auto-clear after 8 seconds
    //   - Cards remain until clearHighlight() is called (infowindow open) or page reload
    setHighlightGrpIds(grpIds);
    setHighlightConvIds(convIds);

    // Cancel any previous column highlight timer, then schedule auto-clear
    if (highlightGrpTimerRef.current) clearTimeout(highlightGrpTimerRef.current);
    if (grpIds.length > 0)
    {
      highlightGrpTimerRef.current = setTimeout(() =>
      {
        setHighlightGrpIds([]);
        highlightGrpTimerRef.current = null;
      }, 8000);
    }

    // Reload groups if there are content updates
    if (toProcess.length > 0)
    {
      onRefreshRef.current(toProcess);
    }

    // OrderDashboard: reload the tab list keeping the current tab selected.
    // Uses onRefreshDashboardOrder (reloadDashboard(false)) so DashboardGroups is not remounted.
    if (hasOrderUpdate)
    {
      onRefreshDashboardOrderRef.current?.();
    }

    // Dashboard-list change: reload the tab list preserving the current selection.
    if (hasDashboardListUpdate)
    {
      onRefreshDashboardListRef.current?.([]);
    }
  }, []);

  /** Clear ALL yellow highlights (columns + cards) */
  const clearHighlight = useCallback(() =>
  {
    if (highlightGrpTimerRef.current)
    {
      clearTimeout(highlightGrpTimerRef.current);
      highlightGrpTimerRef.current = null;
    }
    setHighlightGrpIds([]);
    setHighlightConvIds([]);
  }, []);

  /** Clear yellow highlight for a single conversation card */
  const clearConversationHighlight = useCallback((conversationId: number) =>
  {
    setHighlightConvIds(prev => prev.filter(id => id !== conversationId));
  }, []);

  const pendingGroupIds = useMemo(() => new Set(highlightGrpIds), [highlightGrpIds]);
  const pendingConversationIds = useMemo(() => new Set(highlightConvIds), [highlightConvIds]);

  return {
    hasPendingUpdates: contentPendingCount > 0 || pendingOrderDashboard || pendingDashboardList,
    pendingCount: contentPendingCount,
    pendingOrderDashboard,
    pendingDashboardList,
    clearPending,
    clearHighlight,
    clearConversationHighlight,
    triggerRefresh,
    pendingGroupIds,
    pendingConversationIds,
  };
}
